import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FileUploader } from 'ng2-file-upload';
import { Subscription } from 'rxjs/Subscription';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../../environments/environment';
import { Area } from '../../../../core/constants/area';
import { ConfirmDialogService } from '../../../../core/components/confirm-dialog/confirm-dialog.service';
import { NO_TIMEOUT_HEADER } from '../../../../core/constants/no-timeout-header';

export interface EOMCharge {
	id: number;
	companyId: number;
	unitId: string;
	chargeType: string;
	category: string;
	subCategory: string;
	note: string;
	chargeAmount: number;
	sourceFileName: string;
	sourceRowNumber: number;
	importedDate: string;
	importedByUserId: number;
	isProcessed: boolean;
	processedDate: string;
	invoiceId: number;
	invoiceNumber: string;
	chargeId: number;
	journalEntryCount: number;
	inDownloadLogs: boolean;
	emailQueued: boolean;
}

export interface EOMChargeJournalEntry {
	id: number;
	txnDate: string;
	creditAmount: number;
	debitAmount: number;
	description: string;
	chartOfAccountId: number;
	chartOfAccountName: string;
	chartOfAccountDescription: string;
	journalEntryTypeId: number;
	journalEntryTypeName: string;
}

export interface EOMChargeUploadResult {
	sourceFileName: string;
	rowsImported: number;
	rowsReplaced: number;
	totalChargeAmount: number;
	summary: string;
}

export interface EOMProcessSummary {
	chargeDate: string;
	companiesToProcess: number;
	companiesProcessed: number;
	companiesSkipped: number;
	chargesCreated: number;
	linesCreated: number;
	orphanCharges: number;
	chargeAmountTotal: number;
	taxAmountTotal: number;
	invoiceCount: number;
	rowsLeftUnprocessed: number;
	invoicesAddedToDownloadLogs: number;
	emailsQueued: number;
	errorCompanyId: number;
	errorMessage: string;
}

export interface EOMProcessSkippedCompany {
	companyId: number;
	sourceRows: number;
	totalChargeAmount: number;
	reason: string;
}

export interface EOMProcessInvoice {
	invoiceId: number;
	invoiceNumber: string;
	companyId: number;
	invoiceStatusTypeId: number;
	totalAmountDue: number;
	totalTaxAmountDue: number;
	lineCount: number;
	downloadLogStatus: string;
	emailStatus: string;
}

export interface EOMProcessJournalEntryProblem {
	chargeId: number;
	companyId: number;
	amount: number;
	tax: number;
	entryCount: number;
	totalCredits: number;
	totalDebits: number;
	entriesMissingCOA: number;
	issue: string;
}

export interface EOMProcessRun {
	runId: string;
	kind: string;
	chargeDate: string;
	startedOn: string;
	finishedOn: string;
	isFinished: boolean;
	successful: boolean;
	error: string;
	result: {
		summary: EOMProcessSummary;
		skippedCompanies: EOMProcessSkippedCompany[];
		invoices: EOMProcessInvoice[];
		journalEntryProblems: EOMProcessJournalEntryProblem[];
	};
}

/** The non-permit process handles exactly these categories (transport.spEOMChargeProcess_NonPermits). */
const NON_PERMIT_CATEGORIES = ['samsara', 'pedigree', 'safety / compliance'];

/**
 * Dev Tools > EOM Processing. Uploads the combined EOM charge file
 * (CompanyID, UnitID, ChargeType, Category, SubCatagory, Note, ChargeAmount) into transport.EOMChargeProcess
 * (the table is emptied first), then runs the permit and non-permit processes that turn the rows into
 * invoices, invoice lines and charges. A process runs in the background on the server and is polled here.
 */
@Component({
	selector: 'app-eom-processing',
	templateUrl: './eom-processing.component.html',
	styleUrls: ['./eom-processing.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class EOMProcessingComponent implements OnInit, OnDestroy {
	public uploader: FileUploader;
	public fileDragged = false;
	public file: File = null;
	public previewColumns: string[] = [];
	public previewRows: string[][] = [];
	public previewTotalRows = 0;
	public uploading = false;
	public uploadResult: EOMChargeUploadResult = null;
	public uploadError = '';

	public charges: EOMCharge[] = [];
	public filteredCharges: EOMCharge[] = [];
	public filteredTotal = 0;
	/** Processed rows whose invoice is neither in DownloadLogs nor has an email queued. */
	public notDeliveredCount = 0;
	public loadingCharges = false;
	public chargeFilter = '';
	/** '' = all, 'unprocessed', 'processed'. */
	public statusFilter = '';

	// Unprocessed rows each process would pick up
	public permitRowsToProcess = 0;
	public nonPermitRowsToProcess = 0;
	public otherRowsUnprocessed = 0;

	/** yyyy-MM-dd; defaults to the last day of the current month. */
	public chargeDate = '';
	public startingProcess = false;
	public run: EOMProcessRun = null;

	// Journal entries modal
	@ViewChild('journalEntriesModal') journalEntriesModal: TemplateRef<any>;
	public journalEntriesCharge: EOMCharge = null;
	public journalEntries: EOMChargeJournalEntry[] = [];
	public loadingJournalEntries = false;
	public journalEntriesError = '';
	public journalEntriesCredits = 0;
	public journalEntriesDebits = 0;

	private pollTimer: any = null;
	private pollInFlight = false;
	private pollCount = 0;
	private readonly pollMilliseconds = 3000;
	/** While a process runs, the grid is refreshed every this many polls (3 s each) so its progress shows. */
	private readonly chargesRefreshEveryPolls = 10;
	private chargesSubscription: Subscription = null;
	private readonly previewRowLimit = 10;
	private readonly apiUrl = `${environment.dotNetApiUrl}/${Area.transportation}/EOMProcessing`;
	/** Every request on this page opts out of the site-wide 90 second timeout: it has long running work. */
	private readonly noTimeout = { headers: new HttpHeaders().set(NO_TIMEOUT_HEADER, 'true') };

	constructor(
		private http: HttpClient,
		private toastrService: ToastrService,
		private confirmDialogService: ConfirmDialogService,
		private modalService: NgbModal
	) {}

	ngOnInit() {
		this.uploader = new FileUploader({
			url: '',
			disableMultipart: true,
			filters: [
				{
					name: 'acceptedFileTypes',
					fn: (item: any) => !!item && !!item.name && item.name.toLowerCase().endsWith('.csv'),
				},
			],
		});
		this.uploader.onWhenAddingFileFailed = () => {
			this.toastrService.warning('Only .csv files are accepted.', 'EOM Processing');
		};
		// Fires for a dropped file and for one picked in the file dialog alike.
		this.uploader.onAfterAddingFile = () => {
			setTimeout(() => this.fileChosen());
		};

		const today = new Date();
		this.chargeDate = this.toDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0));

		this.loadCharges();
		this.loadLatestRun();
	}

	ngOnDestroy() {
		this.stopPolling();
		this.cancelChargesRequest();
	}

	get isRunning(): boolean {
		return !!this.run && !this.run.isFinished;
	}

	onDragover() {
		this.fileDragged = true;
	}

	onDragout() {
		this.fileDragged = false;
	}

	/** Called after a file is dropped or picked: keeps the newest one and previews it. */
	fileChosen() {
		this.fileDragged = false;
		const queue = this.uploader.queue;
		if (!queue || queue.length === 0) {
			return;
		}

		const item = queue[queue.length - 1];
		this.uploader.clearQueue();
		this.file = item._file;
		this.uploadResult = null;
		this.uploadError = '';
		this.previewColumns = [];
		this.previewRows = [];
		this.previewTotalRows = 0;

		const reader = new FileReader();
		reader.onload = () => this.buildPreview(reader.result ? reader.result.toString() : '');
		reader.readAsText(this.file);
	}

	resetFile() {
		this.uploader.clearQueue();
		this.file = null;
		this.previewColumns = [];
		this.previewRows = [];
		this.previewTotalRows = 0;
	}

	upload() {
		if (!this.file || this.uploading || this.isRunning) {
			return;
		}

		const unprocessedCount = this.charges.filter((x) => !x.isProcessed).length;
		const message =
			this.charges.length === 0
				? `Upload ${this.file.name} into transport.EOMChargeProcess?`
				: `Uploading ${this.file.name} deletes the ${this.charges.length} row(s) already in transport.EOMChargeProcess` +
				  (unprocessedCount > 0 ? `, including ${unprocessedCount} row(s) that have not been processed yet` : '') +
				  '. Continue?';

		this.confirmDialogService.showConfirm(message, () => this.doUpload(), () => {});
	}

	/** Starts the permit ('permits') or non-permit ('nonpermits') process for the charge date. */
	process(kind: string) {
		if (this.startingProcess || this.isRunning) {
			return;
		}

		if (!this.chargeDate) {
			this.toastrService.warning('Pick a charge date first.', 'EOM Processing');
			return;
		}

		const rows = kind === 'permits' ? this.permitRowsToProcess : this.nonPermitRowsToProcess;
		const name = kind === 'permits' ? 'permit' : 'non-permit';
		const message =
			`Create the ${name} invoices, invoice lines and charges for ${rows} unprocessed row(s) with charge date ${this.chargeDate}?` +
			(kind === 'permits'
				? ' PER invoices are also queued for delivery (DownloadLogs and email).'
				: ' Journal entries are created for every charge.');

		this.confirmDialogService.showConfirm(message, () => this.startProcess(kind), () => {});
	}

	/** Loads the grid; a newer load replaces one still in flight, so the grid always ends on the latest data. */
	loadCharges() {
		this.cancelChargesRequest();
		this.loadingCharges = true;
		this.chargesSubscription = this.http.get<EOMCharge[]>(`${this.apiUrl}/Charges`, this.noTimeout).subscribe(
			(charges) => {
				this.chargesSubscription = null;
				this.charges = charges || [];
				this.loadingCharges = false;
				this.countRowsToProcess();
				this.applyFilter();
			},
			(error) => {
				this.chargesSubscription = null;
				this.loadingCharges = false;
				this.charges = [];
				this.countRowsToProcess();
				this.applyFilter();
				this.toastrService.error(this.errorText(error), 'EOM Processing');
			}
		);
	}

	applyFilter() {
		const filter = (this.chargeFilter || '').trim().toLowerCase();
		this.filteredCharges = this.charges.filter((x) => {
			if (this.statusFilter === 'processed' && !x.isProcessed) {
				return false;
			}
			if (this.statusFilter === 'notdelivered' && !this.isNotDelivered(x)) {
				return false;
			}
			if (this.statusFilter === 'unprocessed' && x.isProcessed) {
				return false;
			}
			if (!filter) {
				return true;
			}
			return [x.companyId, x.unitId, x.chargeType, x.category, x.subCategory, x.note, x.chargeAmount, x.invoiceId, x.invoiceNumber, x.chargeId]
				.map((v) => (v === null || v === undefined ? '' : v.toString().toLowerCase()))
				.some((v) => v.indexOf(filter) >= 0);
		});
		this.filteredTotal = this.filteredCharges.reduce((sum, x) => sum + (x.chargeAmount || 0), 0);
		this.notDeliveredCount = this.charges.filter((x) => this.isNotDelivered(x)).length;
	}

	/**
	 * A processed PERMIT charge whose invoice is neither in DownloadLogs nor has an email queued.
	 * Only permits qualify: the permit process queues its PER invoices for delivery; non-permit (EOM) invoices are delivered later by the EOM job.
	 */
	isNotDelivered(charge: EOMCharge): boolean {
		return !!charge && charge.isProcessed && this.isPermit(charge) && !charge.inDownloadLogs && !charge.emailQueued;
	}

	/** Same rule as the permit process: Category contains "Permit". */
	isPermit(charge: EOMCharge): boolean {
		return (charge.category || '').toLowerCase().indexOf('permit') >= 0;
	}

	/** Switches the grid between only the highlighted rows and all rows. */
	toggleNotDeliveredFilter() {
		this.statusFilter = this.statusFilter === 'notdelivered' ? '' : 'notdelivered';
		this.applyFilter();
	}

	/** Highlights not-delivered rows in the grid. */
	getChargeRowClass = (charge: EOMCharge) => {
		return { 'eom-not-delivered': this.isNotDelivered(charge) };
	};

	/** Opens the modal with the charge's transport.JournalEntries rows. */
	showJournalEntries(charge: EOMCharge) {
		if (!charge || !charge.chargeId) {
			return;
		}

		this.journalEntriesCharge = charge;
		this.journalEntries = [];
		this.journalEntriesError = '';
		this.journalEntriesCredits = 0;
		this.journalEntriesDebits = 0;
		this.loadingJournalEntries = true;

		this.modalService.open(this.journalEntriesModal, { container: '.app', size: 'lg' });

		this.http.get<EOMChargeJournalEntry[]>(`${this.apiUrl}/Charges/${charge.chargeId}/JournalEntries`, this.noTimeout).subscribe(
			(entries) => {
				this.journalEntries = entries || [];
				this.journalEntriesCredits = this.journalEntries.reduce((sum, x) => sum + (x.creditAmount || 0), 0);
				this.journalEntriesDebits = this.journalEntries.reduce((sum, x) => sum + (x.debitAmount || 0), 0);
				this.loadingJournalEntries = false;
			},
			(error) => {
				this.loadingJournalEntries = false;
				this.journalEntriesError = this.errorText(error);
			}
		);
	}

	/** Credits and debits match to the cent. */
	get journalEntriesBalanced(): boolean {
		return Math.round(this.journalEntriesCredits * 100) === Math.round(this.journalEntriesDebits * 100);
	}

	kindName(kind: string): string {
		return kind === 'permits' ? 'Permits' : 'Non-Permits';
	}

	private doUpload() {
		const formData = new FormData();
		formData.append('file', this.file, this.file.name);

		this.uploading = true;
		this.uploadResult = null;
		this.uploadError = '';
		this.http.post<EOMChargeUploadResult>(`${this.apiUrl}/Upload`, formData, this.noTimeout).subscribe(
			(result) => {
				this.uploading = false;
				this.uploadResult = result;
				this.toastrService.success(result.summary, 'EOM Processing');
				this.resetFile();
				this.loadCharges();
			},
			(error) => {
				this.uploading = false;
				this.uploadError = this.errorText(error);
				this.toastrService.error('The EOM charge file was not imported.', 'EOM Processing');
			}
		);
	}

	private startProcess(kind: string) {
		this.startingProcess = true;
		this.http.post<EOMProcessRun>(`${this.apiUrl}/Process`, { kind: kind, chargeDate: this.chargeDate }, this.noTimeout).subscribe(
			(run) => {
				this.startingProcess = false;
				this.run = run;
				this.startPolling();
			},
			(error) => {
				this.startingProcess = false;
				this.toastrService.error(this.errorText(error), 'EOM Processing');
			}
		);
	}

	/** Shows the newest run on the server; resumes polling when it is still running. */
	private loadLatestRun() {
		this.http.get<EOMProcessRun>(`${this.apiUrl}/Process/Latest`, this.noTimeout).subscribe(
			(run) => {
				this.run = run || null;
				if (this.isRunning) {
					this.startPolling();
				}
			},
			() => {}
		);
	}

	private cancelChargesRequest() {
		if (this.chargesSubscription) {
			this.chargesSubscription.unsubscribe();
			this.chargesSubscription = null;
		}
	}

	private startPolling() {
		this.stopPolling();
		this.pollCount = 0;
		this.pollTimer = setInterval(() => this.poll(), this.pollMilliseconds);
	}

	private stopPolling() {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
	}

	private poll() {
		if (!this.run) {
			this.stopPolling();
			return;
		}

		// One status request at a time.
		if (this.pollInFlight) {
			return;
		}

		this.pollInFlight = true;
		this.pollCount++;
		this.http.get<EOMProcessRun>(`${this.apiUrl}/Process/${this.run.runId}`, this.noTimeout).subscribe(
			(run) => {
				this.pollInFlight = false;
				this.run = run;
				if (!run.isFinished) {
					// Show progress: rows turn processed as each company commits. Skipped while a refresh is still loading.
					if (this.pollCount % this.chargesRefreshEveryPolls === 0 && !this.chargesSubscription) {
						this.loadCharges();
					}
					return;
				}

				if (run.isFinished) {
					this.stopPolling();
					this.loadCharges();
					if (run.successful) {
						this.toastrService.success(`${this.kindName(run.kind)} process finished.`, 'EOM Processing');
					} else {
						this.toastrService.error(`${this.kindName(run.kind)} process finished with an error.`, 'EOM Processing');
					}
				}
			},
			(error) => {
				// The server restarted or the run expired: stop polling and say so.
				this.pollInFlight = false;
				this.stopPolling();
				this.toastrService.error(this.errorText(error), 'EOM Processing');
			}
		);
	}

	private countRowsToProcess() {
		this.permitRowsToProcess = 0;
		this.nonPermitRowsToProcess = 0;
		this.otherRowsUnprocessed = 0;
		this.charges
			.filter((x) => !x.isProcessed)
			.forEach((x) => {
				const category = (x.category || '').toLowerCase();
				if (category.indexOf('permit') >= 0) {
					this.permitRowsToProcess++;
				} else if (NON_PERMIT_CATEGORIES.indexOf(category) >= 0) {
					this.nonPermitRowsToProcess++;
				} else {
					this.otherRowsUnprocessed++;
				}
			});
	}

	private toDateInput(date: Date): string {
		const month = ('0' + (date.getMonth() + 1)).slice(-2);
		const day = ('0' + date.getDate()).slice(-2);
		return `${date.getFullYear()}-${month}-${day}`;
	}

	private errorText(error: any): string {
		if (error && typeof error.error === 'string' && error.error.length > 0) {
			return error.error;
		}
		return error && error.message ? error.message : 'The request failed.';
	}

	/** Header and first rows of the CSV, for a look before uploading. Handles quoted fields. */
	private buildPreview(csvText: string) {
		const rows: string[][] = [];
		let row: string[] = [];
		let field = '';
		let inQuotes = false;

		for (let i = 0; i < csvText.length; i++) {
			const c = csvText[i];
			if (inQuotes) {
				if (c === '"' && csvText[i + 1] === '"') {
					field += '"';
					i++;
				} else if (c === '"') {
					inQuotes = false;
				} else {
					field += c;
				}
			} else if (c === '"') {
				inQuotes = true;
			} else if (c === ',') {
				row.push(field);
				field = '';
			} else if (c === '\n' || c === '\r') {
				if (c === '\r' && csvText[i + 1] === '\n') {
					i++;
				}
				row.push(field);
				field = '';
				rows.push(row);
				row = [];
			} else {
				field += c;
			}
		}
		if (field.length > 0 || row.length > 0) {
			row.push(field);
			rows.push(row);
		}

		const nonBlank = rows.filter((r) => r.some((v) => v.trim().length > 0));
		if (nonBlank.length === 0) {
			return;
		}

		// Strip a UTF-8 byte order mark from the first header.
		this.previewColumns = nonBlank[0].map((h, index) => (index === 0 ? h.replace(/^﻿/, '') : h).trim());
		this.previewTotalRows = nonBlank.length - 1;
		this.previewRows = nonBlank.slice(1, 1 + this.previewRowLimit);
	}
}



// WEBPACK FOOTER //
// ./src/app/developer/components/developer-tools/eom-processing/eom-processing.component.ts